/**
 * ChromaticBadge.tsx (GRE-114)
 *
 * A badge indicator for chromatic chord types.
 * Displays type-specific text and color to indicate:
 * - Secondary dominants (V/x)
 * - Neapolitan sixths (bII)
 * - Augmented sixths (+6)
 * - Borrowed chords (mix)
 * - Chromatic mediants (med)
 *
 * Uses unified BADGE_REGISTRY for consistent styling.
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { ChromaticType } from '@/types';
import type { ChromaticBadgeType } from '@/types/harmonic-vocabulary';
import { getBadgeConfig } from '@/data/badge-registry';
import styles from './ChromaticBadge.module.css';

export interface ChromaticBadgeProps {
  /** The chord's chromatic type from legacy Chord type */
  chromaticType: ChromaticType;
  position?: { x: number; y: number };
  isHovered?: boolean;
  onClick?: () => void;
}

/**
 * Map legacy ChromaticType to new ChromaticBadgeType
 */
function mapChromaticType(type: ChromaticType): ChromaticBadgeType {
  switch (type) {
    case 'secondary':
      return 'secondary_dominant';
    case 'neapolitan':
      return 'neapolitan';
    case 'aug6th':
      return 'augmented_sixth';
    case 'borrowed':
      return 'borrowed';
    default:
      return 'borrowed'; // Fallback
  }
}

/**
 * Apple-smooth easing for UI animations
 */
const APPLE_SMOOTH = [0.4, 0.0, 0.2, 1] as const;

export const ChromaticBadge: React.FC<ChromaticBadgeProps> = ({
  chromaticType,
  position = { x: 0, y: 0 },
  isHovered = false,
  onClick,
}) => {
  // Map to new badge type and get config
  const badgeType = mapChromaticType(chromaticType);
  const config = getBadgeConfig(badgeType);

  return (
    <motion.button
      className={styles.magicMomentBadge}
      style={{
        backgroundColor: config.bg,
        top: position.y,
        right: position.x,
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{
        scale: 1,
        opacity: isHovered ? 1 : 0.85,
      }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      transition={{
        duration: 0.3,
        ease: APPLE_SMOOTH,
      }}
      onClick={onClick}
      aria-label={`${badgeType.replace(/_/g, ' ')} chromatic chord`}
    >
      <span className={styles.badgeText}>{config.text}</span>
    </motion.button>
  );
};

ChromaticBadge.displayName = 'ChromaticBadge';

export default ChromaticBadge;
